// src/services/authService.js
import apiClient from "./api"; // Import the configured Axios instance

/**
 * Logs in a user.
 * @param {object} credentials - { email, password }
 * @returns {Promise<object>} User data from the backend on success.
 * @throws {Error} If login fails.
 */

const API = "api";
export const login = async (credentials) => {
  try {
    // The backend automatically sets the HttpOnly cookie on successful login
    const response = await apiClient.post(`${API}/auth/login`, credentials);
    return response.data; // Return the user object sent from the backend
  } catch (error) {
    // Throw a more specific error message from the backend if available
    const message =
      error.response?.data?.message ||
      error.message ||
      "Login failed. Please check your credentials.";
    console.error("Login service error:", message);
    throw new Error(message);
  }
};

/**
 * Signs up a new user.
 * @param {object} details - { name, email, password, role }
 * @returns {Promise<object>} Newly created user data from the backend.
 * @throws {Error} If signup fails.
 */
export const signup = async (details) => {
  try {
    // Backend might automatically log in user and set cookie upon successful signup
    const response = await apiClient.post(`${API}/auth/signup`, details);
    return response.data; // Return the newly created user object
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Signup failed. Please try again.";
    // Handle specific validation errors if backend returns them in errors array
    if (error.response?.data?.errors) {
      const validationErrors = error.response.data.errors
        .map((e) => e.msg)
        .join(", ");
      console.error("Signup validation errors:", validationErrors);
      throw new Error(`Signup failed: ${validationErrors}`);
    }
    console.error("Signup service error:", message);
    throw new Error(message);
  }
};

/**
 * Logs out the current user.
 * @returns {Promise<{success: boolean}>} Confirmation of logout.
 * @throws {Error} If logout fails.
 */
export const logout = async () => {
  try {
    // Call the backend endpoint which clears the HttpOnly cookie
    await apiClient.post(`${API}/auth/logout`);
    // No user data needed from frontend perspective, backend invalidates session
    return { success: true };
  } catch (error) {
    // Even if backend call fails, we might want to proceed with frontend logout.
    // Log the error but don't necessarily block frontend state clearing.
    const message =
      error.response?.data?.message ||
      error.message ||
      "Logout request failed.";
    console.error("Logout service error:", message);
    // Depending on app logic, you might still want to return success or throw
    // throw new Error(message); // Or...
    return { success: true }; // Assume frontend logout should proceed
  }
};

/**
 * Fetches the currently authenticated user's data based on the session cookie.
 * @returns {Promise<object|null>} User data object if authenticated, null otherwise.
 */
export const getCurrentUser = async () => {
  try {
    // Make request to backend - relies on browser sending the HttpOnly cookie
    const response = await apiClient.get(`${API}/auth/me`);
    return response.data; // Returns user object if session is valid
  } catch (error) {
    // A 401 Unauthorized error here means no valid session cookie was sent or it expired
    if (error.response && error.response.status === 401) {
      // console.log("No active session found.");
      return null; // No user logged in
    }
    // Log other unexpected errors
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch current user.";
    console.error("getCurrentUser service error:", message);
    // Depending on requirements, could throw or return null
    // throw new Error(message);
    return null; // Treat other errors as "not logged in" for simplicity here
  }
};
